import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";

// MUI Icons
import NotificationsIcon from "@mui/icons-material/Notifications";
import { BASE_URL } from "../../const/URL";

// ----------------------------------------------------------------------

export type NotificationsPopoverProps = IconButtonProps & {
  customerId?: string;
};

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 5,
    top: 5,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
  },
}));

export function NotificationsPopover({
  sx,
  ...other
}: NotificationsPopoverProps) {
  const navigate = useNavigate();
  const [NotificationsAll, setNotificationsAll] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(
    null
  );

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/v1/notifications/false-read-status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "enterprise_portal_access_Token"
            )}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch Notifications");

      const data = await response.json();

      if (data.isSuccess) {
        setNotificationsAll(data.dataBundle.falseReadStatusCount);
      }
    } catch (err) {
      setError("Failed to load Notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch notifications immediately when the component mounts (auth / loading)
    fetchNotifications();

    // Timer polling turned off — call only on mount / auth load
    // // Set up an interval to fetch notifications every 30 seconds
    // const intervalId = setInterval(fetchNotifications, 30000);
    //
    // // Clear the interval when the component unmounts
    // return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  const totalUnRead = NotificationsAll;

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  return (
    <>
      <IconButton
        color={openPopover ? "primary" : "default"}
        onClick={handleOpenPopover}
        sx={sx}
        {...other}
      >
        <StyledBadge badgeContent={totalUnRead} color="error" max={9}>
          <NotificationsIcon fontSize="medium" />
        </StyledBadge>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: 24,
              border: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          sx={{ py: 2, pl: 2.5, pr: 1.5, bgcolor: "background.neutral" }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Notifications</Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {totalUnRead} unread Notifications
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Divider sx={{ borderStyle: "dashed" }} />

        <Box sx={{ p: 1, bgcolor: "background.neutral" }}>
          <Button
            fullWidth
            color="inherit"
            onClick={() => {
              navigate("/messages/notifications");
              handleClosePopover();
            }}
            sx={{
              fontWeight: "medium",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}
